/**
 * Helpers around `MemberPositionBundle` — in particular for the Decke/Dach slab *surfaces*
 * (`vertical_slabs` / `vertical_roof_slabs`), which have no position grouping of their own: their
 * list edits (Exportiert, Kommentar, Vorbemerkungen) are stored on the bundle of the slab beam.
 */

type SlabSurfaceLike = { guid: string; beam?: { guid?: string } }

/** Position grouping a slab surface's bundle lives in. */
export const SLAB_SURFACE_POSITION_GROUPING_TYPE: Partial<
  Record<ElementTypes, PositionGroupingType>
> = {
  vertical_slabs: 'slab-beams',
  vertical_roof_slabs: 'roof-slab-beams',
}

/** Maps each slab surface guid to its beam guid, the guid the position bundles are keyed by. */
export const buildSlabSurfaceGuidToBeamGuid = (
  ...surfaceGroups: SlabSurfaceLike[][]
): Map<string, string> => {
  const map = new Map<string, string>()
  for (const surfaces of surfaceGroups) {
    for (const surface of surfaces) {
      if (surface.beam?.guid) map.set(surface.guid, surface.beam.guid)
    }
  }
  return map
}

/** All guids that belong to a bundle: the representative and its aggregated members. */
export const bundleMemberGuids = (bundle: MemberPositionBundle): string[] => [
  bundle.representative_position,
  ...(bundle.representative_for ?? []),
]

/** Bundle the element belongs to — as representative or as aggregated child. */
export const bundleForElement = (
  bundles: MemberPositionBundle[] | null | undefined,
  guid: string | undefined,
): MemberPositionBundle | undefined => {
  if (!guid || !bundles?.length) return undefined
  return bundles.find(bundle => bundleMemberGuids(bundle).includes(guid))
}

/** Representative of the bundle the element belongs to — the guid that owns its exported flag. */
export const representativePositionForElement = (
  bundles: MemberPositionBundle[] | null | undefined,
  guid: string | undefined,
): string | undefined => bundleForElement(bundles, guid)?.representative_position

export type SlabSurfaceRow = {
  id: string
  ok?: boolean
  comment?: string
  extended_comment?: string
}

/**
 * Applies the rows mapped into one bundle onto it, field by field: for each of
 * exported/comment/extended_comment, the row (if any) whose value differs from the bundle's
 * stored value wins that field. Grouped slab beams put several surfaces on one bundle, and each of
 * them renders the bundle's values, so different rows can each carry a different edited field in
 * the same autosave call — resolving field by field (instead of picking one row and taking all of
 * its fields) keeps every edit instead of one clobbering the others.
 */
const applyRowsToBundle = (
  bundle: MemberPositionBundle,
  rows: SlabSurfaceRow[],
): MemberPositionBundle => {
  const exportedRow = rows.find(row => !!row.ok !== Boolean(bundle.exported))
  const commentRow = rows.find(row => (row.comment ?? '') !== (bundle.comment ?? ''))
  const extendedCommentRow = rows.find(
    row => (row.extended_comment ?? '') !== (bundle.extended_comment ?? ''),
  )
  if (!exportedRow && !commentRow && !extendedCommentRow) return bundle
  return {
    ...bundle,
    exported: exportedRow ? !!exportedRow.ok : Boolean(bundle.exported),
    comment: commentRow ? commentRow.comment ?? '' : bundle.comment ?? '',
    extended_comment: extendedCommentRow
      ? extendedCommentRow.extended_comment ?? ''
      : bundle.extended_comment ?? '',
  }
}

/**
 * Writes the surface rows' list edits (Exportiert, Kommentar, Vorbemerkungen) onto the slab beam
 * bundles they belong to. Grouping and notes are left untouched, and bundles no row maps into are
 * returned as they are. A beam with no bundle yet (e.g. not yet covered by the stored grouping)
 * gets a fresh default bundle so its row's edits aren't dropped.
 */
export const applySlabSurfaceListEdits = (
  bundles: MemberPositionBundle[],
  items: SlabSurfaceRow[],
  surfaceGuidToBeamGuid: ReadonlyMap<string, string>,
): MemberPositionBundle[] => {
  const coveredBeamGuids = new Set<string>()
  const updated = bundles.map(bundle => {
    const beamGuids = new Set(bundleMemberGuids(bundle))
    beamGuids.forEach(guid => coveredBeamGuids.add(guid))
    const rows = items.filter(item => {
      const beamGuid = surfaceGuidToBeamGuid.get(item.id)
      return !!beamGuid && beamGuids.has(beamGuid)
    })
    return applyRowsToBundle(bundle, rows)
  })

  const rowsByUncoveredBeamGuid = new Map<string, SlabSurfaceRow[]>()
  for (const item of items) {
    const beamGuid = surfaceGuidToBeamGuid.get(item.id)
    if (!beamGuid || coveredBeamGuids.has(beamGuid)) continue
    const rows = rowsByUncoveredBeamGuid.get(beamGuid) ?? []
    rows.push(item)
    rowsByUncoveredBeamGuid.set(beamGuid, rows)
  }
  const created = Array.from(rowsByUncoveredBeamGuid.entries()).map(([beamGuid, rows]) =>
    applyRowsToBundle(
      { representative_position: beamGuid, representative_for: [], exported: false },
      rows,
    ),
  )

  return [...updated, ...created]
}
